'use client';

import { useEffect, useRef } from 'react';

import { usePathname } from '@/i18n/navigation';

/**
 * Hook to restore scroll position to top (accounting for sticky header) when navigating between pages
 */
export function useScrollRestoration() {
  const pathname = usePathname();
  const previousPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip initial mount
    if (previousPathnameRef.current === null) {
      previousPathnameRef.current = pathname;
      return;
    }

    // Only scroll if pathname actually changed
    if (previousPathnameRef.current !== pathname) {
      previousPathnameRef.current = pathname;

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Get header element to calculate its height
        const header = document.querySelector('header');
        const headerHeight = header ? header.offsetHeight : 0;

        // Scroll to top - the sticky header will naturally be at the top
        // Content will start right below the header
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });

        // Also scroll the main content if it exists
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          mainContent.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }

        // Handle hash navigation (anchor links) - scroll to element accounting for header
        if (window.location.hash) {
          const hash = window.location.hash.substring(1);
          const element = document.getElementById(hash);
          if (element) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.pageYOffset - headerHeight;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }
      });
    }
  }, [pathname]);
}
